import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { signIn, type SignInOutput, type SignUpOutput } from "aws-amplify/auth";
import { SignUpForm } from "../components/auth/sign-up-form.js";
import { EmailConfirmationForm } from "../components/auth/email-confirmation-form.js";
import { MfaSetupFlow } from "../components/auth/mfa-setup-flow.js";
import { MfaChallengeForm } from "../components/auth/mfa-challenge-form.js";
import { InvitationPreviewCard } from "../components/auth/invitation-preview-card.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { LoadingScreen } from "../components/loading-screen.js";
import { previewInvitation } from "../lib/api-modules/invitations.js";
import type { InvitationPreview } from "../lib/api-modules/types.js";
import { ApiError } from "../lib/api.js";

/**
 * Sign-up page. State machine:
 *   1. If `?invitation_id=…&token=…` in URL → previewInvitation. On
 *      success render SignUpForm with email read-only. On failure
 *      (404 = bad token, 410 = expired/revoked) render an error state.
 *   2. Otherwise self-signup: render SignUpForm with editable email.
 *   3. After signUp succeeds → render EmailConfirmationForm.
 *   4. After confirmSignUp succeeds → call signIn(creds) → MFA setup
 *      (Cognito's REQUIRED policy will return CONTINUE_SIGN_IN_WITH_TOTP_SETUP).
 *   5. After confirmSignIn (TOTP verify) → refresh + navigate to dashboard.
 */
type Step =
  | { kind: "loading-preview" }
  | { kind: "preview-error"; message: string }
  | { kind: "form" }
  | { kind: "confirm" }
  | { kind: "mfa-setup"; signIn: SignInOutput }
  | { kind: "mfa-challenge" };

export function SignUpPage() {
  const [params] = useSearchParams();
  const invitationId = params.get("invitation_id");
  const token = params.get("token");
  const isInvitationFlow = Boolean(invitationId && token);

  const [step, setStep] = useState<Step>(
    isInvitationFlow ? { kind: "loading-preview" } : { kind: "form" },
  );
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [creds, setCreds] = useState<{ username: string; password: string } | null>(null);

  // Resolve invitation preview on mount (invitation flow only).
  useEffect(() => {
    if (!isInvitationFlow || !invitationId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await previewInvitation(invitationId, token);
        if (cancelled) return;
        setPreview(p);
        setStep({ kind: "form" });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.code === "INVITATION_EXPIRED"
              ? "This invitation has expired. Ask the household admin to send a new one."
              : err.code === "INVITATION_REVOKED"
              ? "This invitation has been revoked."
              : "This invitation link is invalid. Check the URL or ask the household admin to resend."
            : "Couldn't load the invitation. Please try again.";
        setStep({ kind: "preview-error", message });
      }
    })().catch(() => {
      if (!cancelled) {
        setStep({ kind: "preview-error", message: "Couldn't load the invitation." });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isInvitationFlow, invitationId, token]);

  const fixedEmail = useMemo(
    () => (preview ? preview.email : undefined),
    [preview],
  );

  const onSignUpResult = (
    result: SignUpOutput,
    c: { username: string; password: string },
  ) => {
    setCreds(c);
    if (result.isSignUpComplete) {
      // Normally Cognito requires email confirmation, but if the pool
      // is configured for auto-confirmation, fall through to sign-in.
      void onConfirmedFallthrough(c);
      return;
    }
    setStep({ kind: "confirm" });
  };

  const onConfirmedFallthrough = async (c: {
    username: string;
    password: string;
  }) => {
    try {
      const result = await signIn({ username: c.username, password: c.password });
      handleSignInResult(result);
    } catch (err) {
      const code =
        typeof err === "object" && err !== null && "name" in err
          ? String(err.name)
          : "";
      if (code === "UserAlreadyAuthenticatedException") {
        // Hard navigation; the auth context will re-init from storage.
        window.location.assign("/dashboard");
        return;
      }
      throw err;
    }
  };

  const handleSignInResult = (result: SignInOutput) => {
    if (result.isSignedIn) {
      window.location.assign("/dashboard");
      return;
    }
    switch (result.nextStep.signInStep) {
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
        setStep({ kind: "mfa-setup", signIn: result });
        return;
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        setStep({ kind: "mfa-challenge" });
        return;
      default:
        setStep({
          kind: "preview-error",
          message: `Unexpected sign-in step after sign-up: ${result.nextStep.signInStep}`,
        });
    }
  };

  const onMfaDone = () => {
    // Hard navigation (not React Router navigate) so the SPA
    // re-initializes from storage. Amplify v6 confirmSignIn returns
    // before tokens are reliably stored; a same-page navigate races
    // with that, and fetchAuthSession can return empty tokens despite
    // Cognito having issued them. A page reload guarantees Amplify's
    // singleton mounts fresh against the persisted tokens.
    window.location.assign("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[var(--color-page)] text-[var(--color-text)]">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center p-6">
      <div className="mb-8 text-center">
        <h1 className="font-display text-3xl font-medium tracking-tight">
          Hearth
        </h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          {isInvitationFlow
            ? "Accept your invitation"
            : "Create your household"}
        </p>
      </div>

      {step.kind === "loading-preview" && <LoadingScreen label="Loading invitation…" />}

      {step.kind === "preview-error" && (
        <div className="space-y-4">
          <ErrorMessage>{step.message}</ErrorMessage>
          <a
            href="/signup"
            className="block text-center text-sm text-brand-600 hover:underline dark:text-brand-500"
          >
            Start a new household instead
          </a>
        </div>
      )}

      {step.kind === "form" && (
        <div className="space-y-5">
          {preview && <InvitationPreviewCard preview={preview} />}
          <SignUpForm
            {...(fixedEmail !== undefined ? { fixedEmail } : {})}
            onResult={onSignUpResult}
          />
        </div>
      )}

      {step.kind === "confirm" && creds && (
        <EmailConfirmationForm
          username={creds.username}
          onConfirmed={() => onConfirmedFallthrough(creds)}
        />
      )}

      {step.kind === "mfa-setup" && creds && (
        <MfaSetupFlow
          nextStep={
            step.signIn.nextStep as Extract<
              SignInOutput["nextStep"],
              { signInStep: "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" }
            >
          }
          accountName={creds.username}
          onSignedIn={onMfaDone}
        />
      )}

      {step.kind === "mfa-challenge" && (
        <MfaChallengeForm onSignedIn={onMfaDone} />
      )}

      <p className="mt-6 text-center text-sm text-[var(--color-muted)]">
        Already have an account?{" "}
        <a
          href="/signin"
          className="text-[var(--color-accent)] hover:underline"
        >
          Sign in
        </a>
      </p>
      </div>
    </div>
  );
}
